import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMobileMenu } from '../../contexts/MobileMenuContext';
import './Navbar.css';

interface DropdownProps {
  label: string;
  children: React.ReactNode;
  onNavigate: () => void;
}

const NavDropdown: React.FC<DropdownProps> = ({ label, children, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="nav-dropdown" ref={ref}>
      <button
        className="navbar-link nav-dropdown__trigger"
        onClick={() => setOpen(!open)}
      >
        {label}
        <svg className={`nav-dropdown__arrow ${open ? 'nav-dropdown__arrow--open' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="nav-dropdown__menu">
          {React.Children.map(children, child => (
            <div onClick={() => { setOpen(false); onNavigate(); }}>
              {child}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  // Mobile-menu state lives in a context so the in-page mobile toolbar
  // (rendered next to the page title on small screens) can also open it.
  const { open: mobileMenuOpen, toggle: toggleMobileMenu, close: closeMobileMenu } = useMobileMenu();

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to={isAuthenticated ? '/dashboard' : '/races'} className="navbar-logo">
          My Next Run
        </Link>

        {/* Hamburger Menu Button */}
        <button
          className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={`navbar-menu ${mobileMenuOpen ? 'active' : ''} ${!isAuthenticated ? 'navbar-menu--right' : ''}`}>
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="navbar-link" onClick={closeMobileMenu}>
                Dashboard
              </Link>

              <NavDropdown label="Training" onNavigate={closeMobileMenu}>
                <Link to="/training-plan" className="nav-dropdown__link">Training Plan</Link>
                <Link to="/workouts" className="nav-dropdown__link">Workouts</Link>
                <Link to="/race-history" className="nav-dropdown__link">Race History</Link>
                <Link to="/race-goal" className="nav-dropdown__link">Race Goal</Link>
                <Link to="/calculator" className="nav-dropdown__link">Pace Calculator</Link>
              </NavDropdown>

              <NavDropdown label="Races" onNavigate={closeMobileMenu}>
                <Link to="/races" className="nav-dropdown__link">Find a Race</Link>
                <Link to="/recommend" className="nav-dropdown__link">Race Recommender</Link>
              </NavDropdown>

              <Link to="/coach" className="navbar-link" onClick={closeMobileMenu}>
                Coach
              </Link>
            </>
          ) : (
            <>
              <Link to="/races" className="navbar-link" onClick={closeMobileMenu}>
                Find a Race
              </Link>
              <Link to="/calculator" className="navbar-link" onClick={closeMobileMenu}>
                Calculator
              </Link>
            </>
          )}
        </div>

        <div className="navbar-user">
          {isAuthenticated ? (
            <>
              {user?.profile_picture_url && (
                <img
                  src={user.profile_picture_url}
                  alt={`${user.first_name} ${user.last_name}`}
                  className="navbar-avatar"
                />
              )}
              <span className="navbar-username">
                {user?.first_name} {user?.last_name}
              </span>
              <button onClick={logout} className="navbar-logout">
                Logout
              </button>
            </>
          ) : (
            <Link to="/" className="navbar-logout" style={{ textDecoration: 'none' }}>
              Sign In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
